import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Calendar,
  Users,
  Eye,
  CheckCircle2,
  AlertCircle,
  Clock,
  Briefcase,
  TrendingUp,
  ArrowUpDown,
  Building2,
  CalendarClock,
  TrendingDown,
  Minus,
} from 'lucide-react';
import type { ProjectDetails, ProjectStatus, ProjectPriority } from '@/types/project';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface EnhancedProjectCardProps {
  project: ProjectDetails;
  onViewDetails: (project: ProjectDetails) => void;
}

// Enhanced Status Badge with Icons
const StatusBadge: React.FC<{ status: ProjectStatus }> = ({ status }) => {
  const getStatusConfig = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return {
          color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400 border-success-500/20',
          icon: CheckCircle2,
          label: 'Active'
        };
      case 'at-risk':
        return {
          color: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400 border-warning-500/20',
          icon: AlertCircle,
          label: 'At Risk'
        };
      case 'on-hold':
        return {
          color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400 border-neutral-500/20',
          icon: Clock,
          label: 'On Hold'
        };
      default:
        return {
          color: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400 border-info-500/20',
          icon: Briefcase,
          label: status
        };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn("inline-flex items-center gap-1.5 px-2.5 py-1", config.color)}>
      <Icon className="h-3.5 w-3.5" />
      <span className="font-medium">{config.label}</span>
    </Badge>
  );
};

// Enhanced Priority Badge with Icons
const PriorityBadge: React.FC<{ priority: ProjectPriority }> = ({ priority }) => {
  const getPriorityConfig = (priority: ProjectPriority) => {
    switch (priority) {
      case 'high':
        return {
          color: 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-400 border-error-500/20',
          icon: TrendingUp,
          label: 'High Priority'
        };
      case 'medium':
        return {
          color: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400 border-info-500/20',
          icon: ArrowUpDown,
          label: 'Medium'
        };
      case 'low':
        return {
          color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400 border-neutral-500/20',
          icon: ArrowUpDown,
          label: 'Low'
        };
      default:
        return {
          color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400 border-neutral-500/20',
          icon: ArrowUpDown,
          label: priority || 'Unknown'
        };
    }
  };

  const config = getPriorityConfig(priority);
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn("inline-flex items-center gap-1.5 px-2.5 py-1", config.color)}>
      <Icon className="h-3.5 w-3.5" />
      <span className="font-medium">{config.label}</span>
    </Badge>
  );
};

// Variance Badge Component
type VarianceType = 'under-utilized' | 'well-balanced' | 'over-scheduled';

const VarianceBadge: React.FC<{ 
  scheduledHours: number; 
  remainingHours: number;
  compact?: boolean;
}> = ({ scheduledHours, remainingHours, compact = false }) => {
  const scheduledPercentage = remainingHours > 0 
    ? (scheduledHours / remainingHours) * 100 
    : 0;
  
  const getVarianceConfig = (): { type: VarianceType; color: string; icon: any; label: string; description: string } => {
    if (scheduledPercentage < 60) {
      return {
        type: 'under-utilized',
        color: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400 border-warning-500/20',
        icon: TrendingDown,
        label: 'Under-utilized',
        description: 'Capacity available'
      };
    } else if (scheduledPercentage <= 90) {
      return {
        type: 'well-balanced',
        color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400 border-success-500/20',
        icon: Minus,
        label: 'On Track',
        description: 'Well balanced'
      };
    } else {
      return {
        type: 'over-scheduled',
        color: 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-400 border-error-500/20',
        icon: TrendingUp,
        label: 'Over-scheduled',
        description: 'At capacity risk'
      };
    }
  };

  const config = getVarianceConfig();
  const Icon = config.icon;

  if (compact) {
    return (
      <Badge variant="outline" className={cn("inline-flex items-center gap-1 px-2 py-0.5 text-xs", config.color)}>
        <Icon className="h-3 w-3" />
        <span className="font-medium">{config.label}</span>
      </Badge>
    );
  }

  return (
    <div className={cn(
      "inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium",
      config.color
    )}>
      <Icon className="h-4 w-4" />
      <div className="flex flex-col">
        <span className="font-semibold">{config.label}</span>
        <span className="text-xs opacity-90">{config.description}</span>
      </div>
    </div>
  );
};

export const EnhancedProjectCard: React.FC<EnhancedProjectCardProps> = ({ 
  project, 
  onViewDetails 
}) => {
  const budgetPercentage = project.budget && project.budget.totalHours > 0
    ? (project.budget.usedHours / project.budget.totalHours) * 100 
    : 0;

  const isOverBudget = budgetPercentage > 90;
  const isNearBudget = budgetPercentage > 75 && budgetPercentage <= 90;

  return (
    <Card 
      className={cn(
        "group relative overflow-hidden transition-all duration-300",
        "hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1",
        "border-2 hover:border-primary/30",
        "bg-gradient-to-br from-background to-muted/20"
      )}
    >
      {/* Accent bar based on status */}
      <div 
        className={cn(
          "absolute top-0 left-0 right-0 h-1",
          project.status === 'active' && "bg-success-500",
          project.status === 'at-risk' && "bg-warning-500",
          project.status === 'on-hold' && "bg-neutral-500"
        )}
      />

      <CardHeader className="pb-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 flex-1 min-w-0">
            {/* Badges Row */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="font-mono text-xs px-2 py-0.5">
                {project.code}
              </Badge>
              <PriorityBadge priority={project.priority} />
            </div>
            
            {/* Project Name - Enhanced Typography */}
            <CardTitle className="text-xl font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
              {project.name}
            </CardTitle>
            
            {/* Client Name with Icon */}
            <CardDescription className="flex items-center gap-1.5 text-sm">
              <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="line-clamp-1">{project.clientName}</span>
            </CardDescription>
          </div>
          
          {/* Status Badge */}
          <StatusBadge status={project.status} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {project.description}
        </p>

        {/* Key Information with Icons */}
        <div className="space-y-3">
          {/* Timeline */}
          <div className="flex items-center gap-2.5 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">
              {format(project.timeline.startDate, 'MMM d, yyyy')} - {format(project.timeline.endDate, 'MMM d, yyyy')}
            </span>
          </div>

          {/* Team Size */}
          <div className="flex items-center gap-2.5 text-sm">
            <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">
              {project.teamSize} team {project.teamSize === 1 ? 'member' : 'members'}
            </span>
          </div>

          {/* Budget Progress - Primary Metric */}
          {project.budget && (
            <div className="space-y-4 pt-1">
              {/* Budget vs Actual */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground font-medium">Budget vs Actual</span>
                  <span className={cn(
                    "font-bold",
                    isOverBudget && "text-error-600 dark:text-error-400",
                    isNearBudget && "text-warning-600 dark:text-warning-400",
                    !isOverBudget && !isNearBudget && "text-foreground"
                  )}>
                    {Math.round(budgetPercentage)}%
                  </span>
                </div>
                <Progress 
                  value={budgetPercentage} 
                  className={cn(
                    "h-2.5 transition-all",
                    isOverBudget && "[&>div]:bg-error-500",
                    isNearBudget && "[&>div]:bg-warning-500"
                  )}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{project.budget.usedHours.toLocaleString()}h used</span>
                  <span>{project.budget.remainingHours.toLocaleString()}h remaining</span>
                </div>
              </div>

              {/* Scheduled Hours - Secondary Metric */}
              {project.budget.scheduledHours !== undefined && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground font-medium">Scheduled Hours</span>
                    </div>
                    <span className="font-bold text-foreground">
                      {project.budget.remainingHours > 0 
                        ? Math.round((project.budget.scheduledHours / project.budget.remainingHours) * 100) 
                        : 0}%
                    </span>
                  </div>
                  <Progress 
                    value={project.budget.remainingHours > 0 
                      ? (project.budget.scheduledHours / project.budget.remainingHours) * 100 
                      : 0} 
                    className="h-2 transition-all [&>div]:bg-info-500"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{project.budget.scheduledHours.toLocaleString()}h scheduled</span>
                    <span>{Math.max(0, project.budget.remainingHours - project.budget.scheduledHours).toLocaleString()}h available</span>
                  </div>
                </div>
              )}

              {/* Variance Badge */}
              {project.budget.scheduledHours !== undefined && (
                <div className="flex items-center justify-center pt-1">
                  <VarianceBadge 
                    scheduledHours={project.budget.scheduledHours}
                    remainingHours={project.budget.remainingHours}
                    compact={false}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <Separator className="my-4" />

        {/* Footer Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-xs px-2 py-0.5">
              {project.billingType.replace('-', ' ')}
            </Badge>
            {project.billable && (
              <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-success-500/10 text-success-700 dark:text-success-400 border-success-500/20">
                Billable
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(project)}
            className="gap-1.5 group-hover:bg-primary group-hover:text-primary-foreground transition-all"
          >
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">View Details</span>
            <span className="sm:hidden">View</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
